import { ChevronLeft, ChevronRight, Paperclip, Search } from "lucide-react";
import { useLocaleState, useTranslate } from "ra-core";
import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";

import type { MailboxFolderKind, MailboxSummary } from "../mailTypes";
import { useMailboxMessages } from "./useMailbox";

const SEARCH_DELAY_MS = 400;

/** Today: the time; before: the date. */
export const formatListDate = (
  iso: string,
  locale: string,
  now = new Date(),
) => {
  const date = new Date(iso);
  const isToday = date.toDateString() === now.toDateString();
  return new Intl.DateTimeFormat(
    locale,
    isToday
      ? { hour: "2-digit", minute: "2-digit" }
      : { day: "numeric", month: "short" },
  ).format(date);
};

const MessageLine = ({
  message,
  isSent,
  isSelected,
  onOpen,
}: {
  message: MailboxSummary;
  isSent: boolean;
  isSelected: boolean;
  onOpen: () => void;
}) => {
  const translate = useTranslate();
  const [locale = "fr"] = useLocaleState();
  // In « Sent », who it went to matters more than who sent it.
  const who = isSent
    ? translate("crm.mailbox.to_recipients", {
        recipients: message.to
          .map((recipient) => recipient.name || recipient.email)
          .join(", "),
      })
    : message.from?.name || message.from?.email || "";
  return (
    <button
      type="button"
      onClick={onOpen}
      aria-current={isSelected ? "true" : undefined}
      className={cn(
        "flex w-full flex-col gap-0.5 border-b px-3 py-2 text-left text-sm cursor-pointer hover:bg-muted/60",
        isSelected && "bg-muted",
      )}
    >
      <span className="flex items-center gap-2">
        {!message.seen ? (
          <span className="size-2 shrink-0 rounded-full bg-primary" />
        ) : null}
        <span
          className={cn(
            "min-w-0 flex-1 truncate",
            !message.seen && "font-semibold",
          )}
        >
          {who}
        </span>
        {message.has_attachments ? (
          <Paperclip className="size-3.5 shrink-0 text-muted-foreground" />
        ) : null}
        <span className="shrink-0 text-xs text-muted-foreground">
          {formatListDate(message.date, locale)}
        </span>
      </span>
      <span
        className={cn(
          "truncate",
          message.seen ? "text-muted-foreground" : "font-medium",
        )}
      >
        {message.subject || translate("crm.mail.emails.no_subject")}
      </span>
    </button>
  );
};

interface MessageListProps {
  folder: string;
  folderKind: MailboxFolderKind | null;
  page: number;
  q: string;
  selectedUid: number | null;
  onOpen: (uid: number) => void;
  onSearch: (q: string) => void;
  onPage: (page: number) => void;
}

export const MessageList = ({
  folder,
  folderKind,
  page,
  q,
  selectedUid,
  onOpen,
  onSearch,
  onPage,
}: MessageListProps) => {
  const translate = useTranslate();
  const { data, isPending, isError } = useMailboxMessages({ folder, page, q });
  const [search, setSearch] = useState(q);

  useEffect(() => {
    if (search === q) return;
    const timer = setTimeout(() => onSearch(search), SEARCH_DELAY_MS);
    return () => clearTimeout(timer);
  }, [search, q, onSearch]);

  const total = data?.total ?? 0;
  const first = total === 0 ? 0 : (page - 1) * (data?.page_size ?? 25) + 1;
  const last = Math.min(total, page * (data?.page_size ?? 25));

  return (
    <div className="flex min-h-0 flex-col rounded-md border bg-card">
      <div className="relative border-b p-2">
        <Search className="absolute left-4 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          type="search"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder={translate("crm.mailbox.search")}
          aria-label={translate("crm.mailbox.search")}
          className="pl-8"
        />
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto">
        {isPending ? (
          <p className="p-4 text-sm text-muted-foreground">
            {translate("crm.mailbox.loading")}
          </p>
        ) : isError ? (
          <p className="p-4 text-sm text-destructive">
            {translate("crm.mailbox.load_error")}
          </p>
        ) : data.messages.length === 0 ? (
          <p className="p-4 text-sm text-muted-foreground">
            {translate(q ? "crm.mailbox.no_match" : "crm.mailbox.empty")}
          </p>
        ) : (
          <ul aria-label={translate("crm.mailbox.messages")}>
            {data.messages.map((message) => (
              <li key={message.uid}>
                <MessageLine
                  message={message}
                  isSent={folderKind === "sent" || folderKind === "drafts"}
                  isSelected={message.uid === selectedUid}
                  onOpen={() => onOpen(message.uid)}
                />
              </li>
            ))}
          </ul>
        )}
      </div>
      {total > 0 ? (
        <div className="flex items-center gap-2 border-t px-3 py-1.5 text-xs text-muted-foreground">
          <span className="flex-1">
            {translate("crm.mailbox.range", { first, last, total })}
          </span>
          <Button
            variant="ghost"
            size="icon"
            className="size-7"
            disabled={page <= 1}
            onClick={() => onPage(page - 1)}
            aria-label={translate("crm.mailbox.newer")}
          >
            <ChevronLeft className="size-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="size-7"
            disabled={last >= total}
            onClick={() => onPage(page + 1)}
            aria-label={translate("crm.mailbox.older")}
          >
            <ChevronRight className="size-4" />
          </Button>
        </div>
      ) : null}
    </div>
  );
};
